import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import OneSignal from 'react-onesignal';
import type { IInitObject } from 'react-onesignal';

import { useFetchOneSignalIdentityQuery } from '../../ducks/oneSignal.duck';
import { fireAndForget } from './oneSignalSafe';

const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

const ONESIGNAL_INIT_OPTIONS: Omit<IInitObject, 'appId'> = {
  allowLocalhostAsSecureOrigin: process.env.NODE_ENV !== 'production',
  promptOptions: {
    slidedown: {
      prompts: [
        {
          type: 'push',
          autoPrompt: false,
          text: {
            actionMessage: 'Get notified about new offers, replies, and payouts in real time.',
            acceptButton: 'Allow',
            cancelButton: 'Not now',
          },
        },
      ],
    },
  },
};

type AuthState = { auth?: { isAuthenticated?: boolean } };

/**
 * Starts OneSignal for a member, and only for a member.
 *
 * The SDK is initialised the first time this browser has a server-issued
 * identity to log in with, never before. An anonymous visitor that initialises
 * gets a OneSignal user and device subscription of its own, while the account's
 * record already exists: `user-sync-function` creates it with
 * `external_id = notification_user_id` as soon as the account is confirmed. The
 * two then collide, because OneSignal will not move an alias another record
 * holds, and the browser's login is refused with `user-2`.
 *
 * Waiting for provisioning removes the collision rather than recovering from
 * it: one record, created by the server, which this device's subscription joins
 * on login. Identity stays server-owned either way, since the id comes from
 * `app_metadata` signed with the REST key.
 */
const OneSignalProvider = () => {
  const isAuthenticated = useSelector((state: AuthState) => state.auth?.isAuthenticated ?? false);
  const { data } = useFetchOneSignalIdentityQuery(undefined, { skip: !isAuthenticated });
  // Doubles as the record of whether the SDK was ever started: there is nothing
  // to log out of otherwise.
  const initialised = useRef<Promise<void> | null>(null);

  const userId = isAuthenticated ? data?.userId : undefined;
  const authHash = isAuthenticated ? data?.authHash : undefined;

  useEffect(() => {
    if (!ONESIGNAL_APP_ID || typeof window === 'undefined') return;

    let cancelled = false;

    if (userId && authHash) {
      if (!initialised.current) {
        initialised.current = OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          ...ONESIGNAL_INIT_OPTIONS,
        });
      }
      fireAndForget(
        initialised.current.then(() => (cancelled ? undefined : OneSignal.login(userId, authHash)))
      );
    } else if (!isAuthenticated && initialised.current) {
      // Drop the identity so the next member on this browser does not inherit
      // this one's subscription.
      fireAndForget(initialised.current.then(() => OneSignal.logout()));
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId, authHash]);

  return null;
};

export default OneSignalProvider;
